import type { FaqItem } from './services';

export type GuideCostRow = { tier: string; cost: string; includes: string };
export type GuideBreakdownItem = { title: string; range: string; body: string };
export type GuideMaterialGroup = {
  title: string;
  intro: string;
  options: { name: string; range: string; note: string }[];
};
export type GuideBlock = { title: string; body: string };

export type ServiceGuide = {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  hero: { eyebrow: string; title: string; lead: string };
  overview: { title: string; paragraphs: string[] };
  cost: {
    title: string;
    intro: string;
    disclaimer: string;
    rows: GuideCostRow[];
    factorsTitle: string;
    factorsIntro: string;
    factors: string[];
  };
  breakdown: { title: string; intro: string; items: GuideBreakdownItem[] };
  materials: { title: string; intro: string; groups: GuideMaterialGroup[] };
  timelines: {
    title: string;
    intro: string;
    rows: { tier: string; duration: string; note: string }[];
    delaysTitle: string;
    delays: string[];
  };
  included: {
    title: string;
    intro: string;
    checklist: string[];
    groups: GuideBlock[];
  };
  projectTypes: { title: string; intro: string; items: GuideBlock[] };
  styles?: { title: string; intro: string; items: GuideBlock[] };
  process: { title: string; intro: string; steps: { label: string; body: string }[] };
  considerations: { title: string; intro: string; items: GuideBlock[] };
  local: {
    title: string;
    intro: string;
    cities: { name: string; slug: string; body: string }[];
    closing: string;
  };
  gallery: {
    eyebrow: string;
    title: string;
    images: { src: string; alt: string }[];
  };
  faqs: FaqItem[];
  midCta: { title: string; lead: string };
  finalCta: { title: string; lead: string };
};

/** Deep statewide guides for non-kitchen services. Planning ranges, not quotes. */
export const SERVICE_GUIDES: Record<string, ServiceGuide> = {
  'bathroom-remodeling': {
    slug: 'bathroom-remodeling',
    metaTitle: 'Bathroom Remodeling in Texas | 2026 Cost Guide & Quotes | Epic Home Pros',
    metaDescription:
      '2026 Texas bathroom remodel cost guide with planning tiers, waterproofing notes, timelines, and free on-site estimates across major Texas metros.',
    hero: {
      eyebrow: 'Texas Bathroom Remodeling',
      title: 'Bathroom Remodeling Quotes in Texas',
      lead:
        'Walk-in showers, tub conversions, hall baths, and primary suites. Plan with a clear 2026 cost guide, then get a free on-site estimate.',
    },
    overview: {
      title: 'Bathroom remodeling for Texas homeowners',
      paragraphs: [
        'A bathroom remodel can mean a powder-room refresh, a full hall bath rebuild, a tub-to-shower conversion, or a primary suite with a walk-in shower and soaking tub. Wet areas reward careful waterproofing, ventilation, and plumbing planning.',
        'Published 2026 Texas estimates vary by scope, tile, and whether drains or wet walls move. Use the planning ranges below for early budgeting. Your written estimate after an on-site visit is the number for your house.',
        'Epic Home Pros helps homeowners start with one request, an on-site visit, and a free written estimate. You decide whether to move forward.',
      ],
    },
    cost: {
      title: '2026 Texas Bathroom Remodeling Cost Guide',
      intro:
        'These figures are planning ranges for Texas bathroom projects in 2026 - not a quote and not real-time pricing. Waterproofing, plumbing moves, tile labor, and fixture quality usually swing the number more than square footage alone.',
      disclaimer:
        'Two baths of the same size can price very differently. Confirm with a free on-site estimate.',
      rows: [
        {
          tier: 'Powder / half bath',
          cost: '$6,000 - $18,000',
          includes: 'Toilet, vanity, fixtures, flooring, and paint. No shower or tub.',
        },
        {
          tier: 'Cosmetic refresh',
          cost: '$12,000 - $25,000',
          includes: 'Surfaces, fixtures, and vanity updates in the same layout.',
        },
        {
          tier: 'Full hall / guest bath',
          cost: '$25,000 - $50,000',
          includes: 'Full remodel with tile, waterproofing, and fixture updates.',
        },
        {
          tier: 'Tub-to-shower conversion',
          cost: '$8,000 - $20,000',
          includes: 'Standalone conversion with new waterproofing, valve, and glass when scoped alone.',
        },
        {
          tier: 'Primary / ensuite',
          cost: '$45,000 - $90,000',
          includes: 'Walk-in shower, dual vanity options, and larger finish packages.',
        },
        {
          tier: 'Luxury / spa primary',
          cost: '$90,000 - $150,000+',
          includes: 'Curbless wet areas, premium stone, heated floors, and custom cabinetry.',
        },
      ],
      factorsTitle: 'What influences bathroom remodel cost',
      factorsIntro: 'These are the usual drivers that move a project between tiers:',
      factors: [
        'Shower and tub scope - prefab vs tiled walk-in vs freestanding tub',
        'Whether the wet wall or drain location moves',
        'Tile format, mosaic work, and natural stone',
        'Waterproofing system complexity',
        'Vanity size and countertop material',
        'Fixture quality and finish packages',
        'Electrical upgrades, exhaust fan ducting, and heated floors',
        'Permits and inspections when plumbing or electrical changes',
        'Accessibility features such as curb-free entries and grab-bar blocking',
      ],
    },
    breakdown: {
      title: 'Bathroom remodel cost breakdown',
      intro:
        'Where the money goes depends on wet-area scope. Ranges below are typical Texas planning figures for 2026.',
      items: [
        {
          title: 'Tile and waterproofing',
          range: '$2,500 - $12,000+',
          body: 'Often the defining bathroom line. Proper membranes keep water out of framing - especially important in humid Texas climates.',
        },
        {
          title: 'Shower / tub',
          range: '$3,500 - $18,000+',
          body: 'Prefab units sit lower. Tiled walk-ins, frameless glass, and freestanding tubs raise cost quickly.',
        },
        {
          title: 'Vanity and countertop',
          range: '$1,500 - $8,000+',
          body: 'Stock single vanities at the low end; double vanities and stone tops sit higher.',
        },
        {
          title: 'Plumbing fixtures',
          range: '$800 - $4,000+',
          body: 'Valves, showerheads, tub fillers, and faucets. Multi-head systems and designer finishes add cost.',
        },
        {
          title: 'Toilet',
          range: '$300 - $1,800',
          body: 'Standard two-piece models at the low end; comfort-height and wall-hung units cost more.',
        },
        {
          title: 'Flooring',
          range: '$1,200 - $5,000',
          body: 'Tile and LVP are common. Heated-floor mats are a frequent primary-bath upgrade.',
        },
        {
          title: 'Lighting and exhaust',
          range: '$600 - $3,000',
          body: 'Vanity lighting plus a correctly sized exhaust fan ducted outdoors matters in Texas humidity.',
        },
        {
          title: 'Electrical',
          range: '$1,000 - $4,500',
          body: 'GFCI protection, dedicated circuits, and fan ducting. Older homes may need more capacity.',
        },
        {
          title: 'Labor / project management',
          range: '$6,000 - $20,000+',
          body: 'Demo, waterproofing, tile, plumbing, electrical, glass, and coordination.',
        },
        {
          title: 'Permits',
          range: 'When required by scope',
          body: 'Like-for-like cosmetic swaps may need fewer permits than drain or wet-wall moves.',
        },
        {
          title: 'Glass and accessories',
          range: 'Varies by package',
          body: 'Frameless shower glass, niches, benches, and hardware finishes complete the room.',
        },
        {
          title: 'Contingency',
          range: 'Plan a buffer',
          body: 'Hidden water damage behind showers is common after demolition. A contingency keeps work moving.',
        },
      ],
    },
    materials: {
      title: 'Tile, vanities, waterproofing, and finishes',
      intro: 'Finish choices and waterproofing details drive both durability and budget.',
      groups: [
        {
          title: 'Shower and wet-area options',
          intro: 'Wet areas are where bathrooms succeed or fail long-term.',
          options: [
            {
              name: 'Tub-shower combo',
              range: 'Common hall-bath path',
              note: 'Familiar layout with tile surround upgrades available.',
            },
            {
              name: 'Tiled walk-in shower',
              range: 'Mid to premium',
              note: 'Custom pan, waterproofing, and glass drive cost and look.',
            },
            {
              name: 'Tub-to-shower conversion',
              range: '$8,000 - $20,000 alone',
              note: 'Removes the tub and builds a walk-in with new waterproofing.',
            },
            {
              name: 'Curbless / low-threshold',
              range: 'Premium detailing',
              note: 'Better aging-in-place access; needs careful floor pitch and membrane work.',
            },
          ],
        },
        {
          title: 'Vanity and surface options',
          intro: 'Storage and counters shape how the bath works daily.',
          options: [
            {
              name: 'Stock vanity',
              range: 'Budget-friendly',
              note: 'Faster availability for standard widths.',
            },
            {
              name: 'Double vanity',
              range: 'Primary-bath favorite',
              note: 'Needs wall width and plumbing clearances.',
            },
            {
              name: 'Quartz or stone tops',
              range: 'Mid to premium',
              note: 'Durable tops with sink cutouts planned to the vanity.',
            },
            {
              name: 'Tile formats',
              range: 'Labor varies',
              note: 'Large-format, mosaics, and natural stone change install time and cost.',
            },
          ],
        },
      ],
    },
    timelines: {
      title: 'Bathroom remodel timelines',
      intro:
        'Schedules are approximate for 2026 Texas projects. Tile, glass, and plumbing inspections often set the pace.',
      rows: [
        { tier: 'Powder refresh', duration: '1 - 2 weeks', note: 'Limited wet-area work.' },
        { tier: 'Cosmetic full bath', duration: '2 - 4 weeks', note: 'Same layout, surface and fixture updates.' },
        { tier: 'Tub-to-shower conversion', duration: '2 - 3 weeks', note: 'When scoped as a standalone job.' },
        { tier: 'Full hall bath', duration: '3 - 5 weeks', note: 'Full waterproofing and tile rebuild.' },
        { tier: 'Primary suite', duration: '4 - 7 weeks', note: 'Larger finish package and more trades.' },
        { tier: 'Luxury / layout change', duration: '6 - 10+ weeks', note: 'Plumbing moves and custom finishes.' },
      ],
      delaysTitle: 'What commonly extends the schedule',
      delays: [
        'Specialty tile and glass lead times',
        'Moving drains or wet walls',
        'Permit and inspection timing',
        'Hidden moisture damage after demolition',
        'Fixture backorders',
        'Change orders after waterproofing starts',
      ],
    },
    included: {
      title: 'What a bathroom remodel may include',
      intro: 'Every written estimate should spell out inclusions. Common categories include:',
      checklist: [
        'Design and planning',
        'Demolition and haul-away',
        'Waterproofing',
        'Tile and shower/tub work',
        'Vanity and countertop',
        'Plumbing fixtures',
        'Toilet',
        'Flooring',
        'Lighting and exhaust',
        'Electrical updates',
        'Glass and accessories',
        'Cleanup and final walkthrough',
      ],
      groups: [
        {
          title: 'Standard scope items',
          body: 'Finish and fixture work in the existing footprint - vanity, tile surround, fixtures, paint, and lighting with the wet wall in place.',
        },
        {
          title: 'Optional upgrades',
          body: 'Frameless glass, heated floors, niche packages, comfort-height toilets, and premium fixture finishes.',
        },
        {
          title: 'Structural or relocation work',
          body: 'Moving drains, expanding wet areas, curb-free conversions, or reworking walls. Needs careful on-site review.',
        },
      ],
    },
    projectTypes: {
      title: 'Types of bathroom remodels',
      intro: 'Name the project type early so budget and visit planning stay accurate.',
      items: [
        { title: 'Powder room refresh', body: 'Half bath updates without shower or tub work.' },
        { title: 'Hall / guest bath', body: 'Full remodel of a standard family bath.' },
        { title: 'Primary / ensuite', body: 'Larger footprint with walk-in shower and dual vanity options.' },
        { title: 'Tub-to-shower conversion', body: 'Replace a tub with a walk-in shower and new waterproofing.' },
        { title: 'Accessibility-minded bath', body: 'Lower thresholds, grab-bar blocking, and clearer clearances where space allows.' },
        { title: 'Full gut with layout change', body: 'Rework wet walls, fixtures, and storage when the house supports it.' },
      ],
    },
    process: {
      title: 'Bathroom remodeling process with Epic Home Pros',
      intro: 'One request, an on-site visit, and a free written estimate. If you proceed, design and construction follow the plan you approve.',
      steps: [
        {
          label: '1. Initial inquiry',
          body: 'Share city, hall bath vs primary suite, and goals such as walk-in shower or tub conversion.',
        },
        {
          label: '2. Project discussion',
          body: 'We follow up - typically within one business day - to clarify scope and schedule a visit.',
        },
        {
          label: '3. On-site estimate',
          body: 'We measure wet walls, check plumbing access, and discuss waterproofing and finish preferences.',
        },
        {
          label: '4. Written estimate',
          body: 'You receive a written estimate. Estimates are free with no obligation to move forward.',
        },
        {
          label: '5. Design and selections',
          body: 'If you proceed, tile, vanity, fixtures, and glass selections lock the final scope.',
        },
        {
          label: '6. Construction and completion',
          body: 'Work moves from demolition through waterproofing, install, and final walkthrough.',
        },
      ],
    },
    considerations: {
      title: 'What to consider before remodeling',
      intro: 'A little clarity before the visit makes the estimate more accurate.',
      items: [
        {
          title: 'Keep or move the wet wall',
          body: 'Same-layout updates cost less than relocating drains or expanding the shower.',
        },
        {
          title: 'Ventilation',
          body: 'A ducted exhaust fan matters in Texas humidity to protect grout and finishes.',
        },
        {
          title: 'Tile maintenance',
          body: 'Grout color, sealers, and tile size affect cleaning and long-term look.',
        },
        {
          title: 'Daily storage',
          body: 'Vanity drawers, linen storage, and niches should match how the household uses the room.',
        },
        {
          title: 'Aging-in-place options',
          body: 'Comfort-height toilets, grab-bar blocking, and lower thresholds are easier to plan before tile starts.',
        },
        {
          title: 'Budget tier',
          body: 'Use the 2026 Texas planning tiers on this page, then refine with an on-site estimate.',
        },
      ],
    },
    local: {
      title: 'Bathroom remodeling across Texas',
      intro: 'Epic Home Pros serves bathroom remodeling across major Texas metros. Humidity, housing age, and finish expectations differ by city.',
      cities: [
        {
          name: 'Austin',
          slug: 'austin',
          body: 'Austin baths range from compact hall baths in older homes to open primary suites in newer builds. Waterproofing and ventilation matter in Central Texas humidity.',
        },
        {
          name: 'Dallas',
          slug: 'dallas',
          body: 'DFW projects often update dated hall baths and expand primary suites. Older plumbing access should be checked on site before layout moves.',
        },
        {
          name: 'Houston',
          slug: 'houston',
          body: 'Greater Houston baths benefit from durable finishes and strong ventilation for Gulf Coast humidity.',
        },
        {
          name: 'San Antonio',
          slug: 'san-antonio',
          body: 'San Antonio remodels often focus on durable tile, better storage, and practical walk-in shower upgrades.',
        },
      ],
      closing:
        'Nearby metros may also be covered. Request a quote with your city and we will confirm coverage.',
    },
    gallery: {
      eyebrow: 'Bathroom inspiration',
      title: 'Showers, vanities, and finishes homeowners are planning',
      images: [
        {
          src: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1200&q=80&auto=format&fit=crop',
          alt: 'Walk-in shower with marble tile',
        },
        {
          src: 'https://images.unsplash.com/photo-1507652313519-d4e9174996dd?w=1200&q=80&auto=format&fit=crop',
          alt: 'Freestanding soaking tub',
        },
        {
          src: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=1200&q=80&auto=format&fit=crop',
          alt: 'Bathroom vanity with stone top',
        },
        {
          src: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=80&auto=format&fit=crop',
          alt: 'Bright modern bathroom finishes',
        },
      ],
    },
    faqs: [
      {
        question: 'How much does a bathroom remodel cost in Texas in 2026?',
        answer:
          'Planning ranges on this site run about $6,000-$18,000 for powder rooms, $12,000-$25,000 for cosmetic refreshes, $25,000-$50,000 for full hall baths, $45,000-$90,000 for many primary suites, and $90,000-$150,000+ for luxury spa work. Tub-to-shower conversions often plan around $8,000-$20,000 when scoped alone. These are planning estimates, not quotes.',
      },
      {
        question: 'What usually drives bathroom remodel cost?',
        answer:
          'Waterproofing, plumbing moves, tile labor, shower glass, and fixture quality. Layout changes cost more than same-footprint updates.',
      },
      {
        question: 'How long does a bathroom remodel take?',
        answer:
          'Powder refreshes can finish in 1-2 weeks. Full hall baths often take 3-5 weeks. Primary and luxury projects commonly need 4-10+ weeks depending on materials and inspections.',
      },
      {
        question: 'Can I convert a tub to a walk-in shower?',
        answer:
          'Yes. An on-site visit confirms drain location, waterproofing needs, and whether the pan and walls need a full rebuild.',
      },
      {
        question: 'Is the bathroom estimate free?',
        answer: 'Yes. Free on-site estimate with no obligation to move forward.',
      },
      {
        question: 'Which Texas cities do you cover?',
        answer:
          'Austin, Dallas, Houston, San Antonio, and surrounding metros when coverage allows. Include your city on the quote form.',
      },
    ],
    midCta: { title: 'Ready to price your bathroom?', lead: 'Free on-site estimate. No obligation.' },
    finalCta: {
      title: 'Start your Texas bathroom remodel quote',
      lead: 'Tell us hall bath, primary suite, or conversion goals. We will help with a free on-site estimate.',
    },
  },

  'adu-construction': {
    slug: 'adu-construction',
    metaTitle: 'ADU Construction in Texas | 2026 Cost Guide & Quotes | Epic Home Pros',
    metaDescription:
      '2026 Texas ADU cost guide for garage conversions, detached cottages, and attached suites. Free lot visits and written estimates across major metros.',
    hero: {
      eyebrow: 'Texas ADU Construction',
      title: 'ADU quotes with free on-site estimates',
      lead:
        'Detached cottages, garage conversions, and attached suites. Plan with a clear 2026 cost guide, then walk the lot.',
    },
    overview: {
      title: 'ADU construction for Texas homeowners',
      paragraphs: [
        'An ADU is a permitting and site project, not a weekend build. Feasibility depends on setbacks, utilities, parking rules, access, and how the structure will be used.',
        '2026 Texas planning ranges vary widely by size, foundation, utility connections, and finish level. Garage conversions can cost less than new detached builds. Your written estimate after a lot visit is the number for your property.',
        'Epic Home Pros helps homeowners start with one request, a lot walk when needed, and a free written estimate. You decide whether to move forward.',
      ],
    },
    cost: {
      title: '2026 Texas ADU Cost Guide',
      intro:
        'These figures are planning ranges for Texas ADU projects in 2026 - not a quote for your lot. Size, site work, utilities, and finishes move the number more than floor plans alone.',
      disclaimer:
        'City rules and utility access vary. Confirm feasibility with a free on-site lot visit.',
      rows: [
        {
          tier: 'Garage conversion',
          cost: 'Often lower than new build',
          includes: 'Insulation, egress, finishes, and utility upgrades as needed inside an existing garage.',
        },
        {
          tier: 'Compact detached ADU',
          cost: 'Lower end of new-build range',
          includes: 'Smaller footprint with simpler finish package and limited site work.',
        },
        {
          tier: 'Typical detached / attached',
          cost: '$180,000 - $350,000+',
          includes: 'New structure with foundation, utilities, and mid-range finishes.',
        },
        {
          tier: 'Larger / higher-finish ADU',
          cost: '$350,000+',
          includes: 'Bigger footprint, premium finishes, complex site work, or specialty systems.',
        },
      ],
      factorsTitle: 'What influences ADU cost',
      factorsIntro: 'These drivers usually move a project between tiers:',
      factors: [
        'Conversion vs new detached or attached build',
        'Square footage and bedroom/bath count',
        'Foundation and site grading',
        'Utility extensions for water, sewer, electric, and gas',
        'Setback, height, and parking constraints',
        'Access for equipment and materials',
        'Finish level inside the ADU',
        'Design, engineering, and permitting requirements',
        'Contingency for unknown utility or soil conditions',
      ],
    },
    breakdown: {
      title: 'ADU cost breakdown',
      intro: 'Major line items stack differently on every lot. Ranges below are typical Texas planning context for 2026.',
      items: [
        {
          title: 'Design and engineering',
          range: 'Varies by complexity',
          body: 'Plans, structural notes, and documents needed for permitting.',
        },
        {
          title: 'Site work and foundation',
          range: 'Major new-build driver',
          body: 'Grading, slabs or pier systems, drainage, and access improvements.',
        },
        {
          title: 'Utilities',
          range: 'Often underestimated',
          body: 'Water, sewer or septic connections, electrical service, and gas where needed.',
        },
        {
          title: 'Structure and framing',
          range: 'Size driven',
          body: 'Walls, roof, windows, and exterior shell for detached or attached units.',
        },
        {
          title: 'Interior finishes',
          range: 'Package driven',
          body: 'Kitchenette, bath, flooring, paint, and fixtures inside the ADU.',
        },
        {
          title: 'Garage conversion upgrades',
          range: 'Condition driven',
          body: 'Insulation, moisture control, egress windows or doors, and slab or wall improvements.',
        },
        {
          title: 'Permits and inspections',
          range: 'City dependent',
          body: 'Local review timelines and requirements vary by municipality.',
        },
        {
          title: 'Contingency',
          range: 'Plan a buffer',
          body: 'Unknown soils, utility depths, or HOA requirements can change scope after kickoff.',
        },
      ],
    },
    materials: {
      title: 'ADU types and finish decisions',
      intro: 'Choose the ADU path that fits the lot before locking finishes.',
      groups: [
        {
          title: 'ADU project paths',
          intro: 'Each path has different cost and permitting implications.',
          options: [
            {
              name: 'Garage conversion',
              range: 'Often least site work',
              note: 'Uses existing structure but still needs insulation, egress, and utility upgrades.',
            },
            {
              name: 'Detached backyard cottage',
              range: 'Full new build',
              note: 'Most flexible layout; setbacks and utilities dominate feasibility.',
            },
            {
              name: 'Attached suite',
              range: 'Shared-wall detailing',
              note: 'May connect to the main house for utilities and circulation.',
            },
            {
              name: 'Guest house / in-law suite',
              range: 'Use-case driven',
              note: 'Plan kitchenette, bath, and privacy needs around household goals.',
            },
          ],
        },
        {
          title: 'Finish level choices',
          intro: 'Interior package quality swings total cost after the shell is set.',
          options: [
            {
              name: 'Essential finishes',
              range: 'Lower package',
              note: 'Durable, simple materials that keep the ADU functional.',
            },
            {
              name: 'Mid-range finishes',
              range: 'Common target',
              note: 'Better cabinetry, fixtures, and flooring without luxury pricing.',
            },
            {
              name: 'Premium finishes',
              range: 'Highest package',
              note: 'Custom millwork and higher-end appliances raise both cost and lead time.',
            },
          ],
        },
      ],
    },
    timelines: {
      title: 'ADU project timelines',
      intro:
        'ADU schedules include design and permitting before construction. Windows below are approximate for 2026 Texas projects - not guarantees.',
      rows: [
        {
          tier: 'Garage conversion',
          duration: 'Often shorter build phase',
          note: 'Still needs design, permits, and utility checks first.',
        },
        {
          tier: 'Compact detached ADU',
          duration: 'Multi-month process',
          note: 'Design, permits, site work, and build stacked end to end.',
        },
        {
          tier: 'Typical detached / attached',
          duration: 'Longer multi-month process',
          note: 'Utility extensions and inspections add calendar time.',
        },
        {
          tier: 'Complex or high-finish ADU',
          duration: 'Longest path',
          note: 'Engineering, HOA, or specialty systems extend the schedule.',
        },
      ],
      delaysTitle: 'What commonly extends ADU schedules',
      delays: [
        'City permitting and inspection cycles',
        'Utility connection timelines',
        'HOA architectural review where applicable',
        'Material lead times for windows and cabinets',
        'Weather during foundation and framing',
        'Design changes after permit submittal',
      ],
    },
    included: {
      title: 'What an ADU project may include',
      intro: 'Scopes vary by path. Common categories include:',
      checklist: [
        'Lot evaluation',
        'Design and planning',
        'Permitting support path',
        'Site work and foundation',
        'Utility connections',
        'Structure and exterior',
        'Interior kitchenette and bath',
        'Electrical and HVAC as scoped',
        'Finishes and fixtures',
        'Inspections coordination',
        'Cleanup',
        'Final walkthrough',
      ],
      groups: [
        {
          title: 'Standard planning items',
          body: 'Lot walk, feasibility notes, and a written estimate that separates conversion vs new-build paths.',
        },
        {
          title: 'Optional upgrades',
          body: 'Expanded kitchenettes, outdoor living connections, premium finishes, and extra storage.',
        },
        {
          title: 'Site-critical work',
          body: 'Utility extensions, drainage corrections, and structural solutions required by the lot.',
        },
      ],
    },
    projectTypes: {
      title: 'Types of ADU projects',
      intro: 'Start by naming the structure type and intended use.',
      items: [
        { title: 'Detached backyard cottage', body: 'Separate structure for guests, family, or flexible living.' },
        { title: 'Garage conversion', body: 'Reuse existing garage volume with insulation, egress, and finishes.' },
        { title: 'Attached in-law suite', body: 'Connected living with shared or separate entries.' },
        { title: 'Guest house', body: 'Short-stay or family lodging with a private bath and kitchenette.' },
        { title: 'Future-ready flex space', body: 'Designed for changing household needs over time.' },
      ],
    },
    process: {
      title: 'ADU process with Epic Home Pros',
      intro: 'One request, a lot visit when needed, and a free written estimate before you commit to a path.',
      steps: [
        {
          label: '1. Initial inquiry',
          body: 'Share city, lot notes, and whether you want a conversion or new structure.',
        },
        {
          label: '2. Project discussion',
          body: 'We follow up within about one business day to clarify goals and coverage.',
        },
        {
          label: '3. Lot visit',
          body: 'We review setbacks, utilities, access, and what looks realistic for the property.',
        },
        {
          label: '4. Written estimate',
          body: 'You receive a written estimate. Free, with no obligation to move forward.',
        },
        {
          label: '5. Design and permits path',
          body: 'If you proceed, design documents and local permitting steps follow the approved plan.',
        },
        {
          label: '6. Build and completion',
          body: 'Construction, inspections, and final walkthrough wrap the project.',
        },
      ],
    },
    considerations: {
      title: 'What to consider before an ADU',
      intro: 'Lot realities matter more than mood boards.',
      items: [
        {
          title: 'Setbacks and height',
          body: 'Local rules can block a preferred footprint even when the yard looks large.',
        },
        {
          title: 'Utilities',
          body: 'Water, sewer, and electric connections often decide conversion vs new-build cost.',
        },
        {
          title: 'Parking and access',
          body: 'Some cities require parking or restrict alley and side-yard access.',
        },
        {
          title: 'Intended use',
          body: 'Family guest space vs long-term occupancy changes layout and finish priorities.',
        },
        {
          title: 'Budget band',
          body: 'Use the 2026 Texas planning tiers, then refine after a lot visit.',
        },
        {
          title: 'Timeline patience',
          body: 'Design and permits usually take real calendar time before construction starts.',
        },
      ],
    },
    local: {
      title: 'ADU projects across Texas',
      intro: 'Rules and lot patterns differ by metro. An on-site walk keeps the plan honest.',
      cities: [
        {
          name: 'Austin',
          slug: 'austin',
          body: 'Austin ADU interest is high across varied lot sizes. Setbacks, utilities, and parking rules should be checked early.',
        },
        {
          name: 'Dallas',
          slug: 'dallas',
          body: 'DFW lots range from tight in-town parcels to larger suburban yards. Access and utility paths vary widely.',
        },
        {
          name: 'Houston',
          slug: 'houston',
          body: 'Greater Houston ADU planning often includes drainage and utility access alongside setbacks.',
        },
        {
          name: 'San Antonio',
          slug: 'san-antonio',
          body: 'San Antonio projects usually start with a realistic lot walk before finish selections.',
        },
      ],
      closing: 'Request a quote with your city and lot notes so we can confirm coverage and next steps.',
    },
    gallery: {
      eyebrow: 'ADU inspiration',
      title: 'Backyard homes and conversions homeowners are planning',
      images: [
        {
          src: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80&auto=format&fit=crop',
          alt: 'Modern house exterior with backyard living',
        },
        {
          src: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80&auto=format&fit=crop',
          alt: 'Cottage-style accessory dwelling',
        },
        {
          src: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&q=80&auto=format&fit=crop',
          alt: 'Guest suite living space',
        },
        {
          src: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200&q=80&auto=format&fit=crop',
          alt: 'Covered outdoor connection near an ADU',
        },
      ],
    },
    faqs: [
      {
        question: 'How much does an ADU cost in Texas in 2026?',
        answer:
          'Many new detached or attached ADUs plan around $180,000-$350,000+, with larger or higher-finish projects above that. Garage conversions can cost less. These are planning ranges, not quotes. A lot visit sets the real number.',
      },
      {
        question: 'Do ADU rules differ by Texas city?',
        answer:
          'Yes. Setbacks, parking, height, and utility connections vary by city and sometimes by neighborhood. Walk the lot before locking a plan.',
      },
      {
        question: 'Garage conversion or new detached build - which is simpler?',
        answer:
          'Conversions can involve less new foundation work, but still need insulation, egress, and utilities. Detached builds add site work and often more permitting. Compare both for your lot.',
      },
      {
        question: 'Is the ADU estimate free?',
        answer: 'Yes. Free estimate with no obligation to move forward.',
      },
    ],
    midCta: { title: 'Ready to talk through an ADU?', lead: 'Free estimate. No obligation.' },
    finalCta: {
      title: 'Start your Texas ADU quote',
      lead: 'Share lot notes and goals. We will help with a free on-site estimate path.',
    },
  },

  'outdoor-living': {
    slug: 'outdoor-living',
    metaTitle: 'Outdoor Living in Texas | 2026 Cost Guide & Quotes | Epic Home Pros',
    metaDescription:
      '2026 Texas outdoor living cost guide for decks, covered patios, pergolas, and outdoor kitchens. Free on-site estimates across major metros.',
    hero: {
      eyebrow: 'Texas Outdoor Living',
      title: 'Decks, patios, and pergolas built for Texas weather',
      lead:
        'Plan decks, covered patios, pergolas, and outdoor kitchens with a clear 2026 cost guide - then get a free on-site yard estimate.',
    },
    overview: {
      title: 'Outdoor living for Texas homeowners',
      paragraphs: [
        'Texas outdoor rooms need shade, drainage, and materials that handle heat, sun, and summer storms. A good plan starts with how you use the yard - not a catalog photo.',
        '2026 planning ranges vary by structure type, cover, electrical or gas needs, and site conditions. Your written estimate after an on-site visit prices your lot.',
        'Epic Home Pros helps homeowners start with one request, a yard walk, and a free written estimate. You decide whether to move forward.',
      ],
    },
    cost: {
      title: '2026 Texas Outdoor Living Cost Guide',
      intro:
        'These figures are planning ranges for Texas outdoor projects in 2026 - not a quote for your yard. Roof structure, utilities, and finish level move the number quickly.',
      disclaimer: 'Confirm with a free on-site estimate that accounts for sun, drainage, and access.',
      rows: [
        {
          tier: 'Ground-level deck',
          cost: '$8,000 - $20,000+',
          includes: 'Composite or wood decking with basic railings depending on size and material.',
        },
        {
          tier: 'Elevated or larger deck',
          cost: 'Higher with structure',
          includes: 'Stairs, railings, and framing for slope or second-level access.',
        },
        {
          tier: 'Pergola / shade structure',
          cost: 'Varies by size and attachments',
          includes: 'Freestanding or attached shade with optional lighting.',
        },
        {
          tier: 'Covered patio',
          cost: 'Climbs with roof system',
          includes: 'Solid cover, posts, and weather protection sized to the house and yard.',
        },
        {
          tier: 'Outdoor kitchen',
          cost: 'Climbs with utilities',
          includes: 'Counters, appliances, gas and electrical, and weather-rated finishes.',
        },
      ],
      factorsTitle: 'What influences outdoor living cost',
      factorsIntro: 'These drivers usually move a project between tiers:',
      factors: [
        'Deck height and framing complexity',
        'Material choice - cedar, composite, or hardwood',
        'Covered roof structure vs open pergola',
        'Electrical and gas runs for kitchens or lighting',
        'Drainage and grading corrections',
        'Railings, stairs, and built-in seating',
        'Access for materials and equipment',
        'Permit requirements for covered structures where applicable',
      ],
    },
    breakdown: {
      title: 'Outdoor living cost breakdown',
      intro: 'Line items stack differently by project type. Ranges below are Texas planning context for 2026.',
      items: [
        {
          title: 'Decking materials',
          range: 'Material driven',
          body: 'Cedar, composite, and hardwood differ in price, heat feel, and maintenance.',
        },
        {
          title: 'Framing and structure',
          range: 'Height driven',
          body: 'Footings, joists, and posts for elevated decks or patio covers.',
        },
        {
          title: 'Cover / roof system',
          range: 'Major patio driver',
          body: 'Solid covers cost more than open shade and need proper attachment detailing.',
        },
        {
          title: 'Outdoor kitchen package',
          range: 'Appliance and utility driven',
          body: 'Grill islands, counters, sinks, and weather-rated cabinets.',
        },
        {
          title: 'Electrical and gas',
          range: 'Often required',
          body: 'Outdoor-rated circuits, lighting, and gas lines for kitchens or fire features.',
        },
        {
          title: 'Railings and stairs',
          range: 'Code and style driven',
          body: 'Safety rails, stair runs, and cable or picket options.',
        },
        {
          title: 'Drainage and site prep',
          range: 'Lot dependent',
          body: 'Grading and water management protect the structure and the house.',
        },
        {
          title: 'Labor / project management',
          range: 'Scope driven',
          body: 'Site work, carpentry, and utility coordination for the full outdoor room.',
        },
      ],
    },
    materials: {
      title: 'Materials for Texas heat, sun, and storms',
      intro: 'Choose products for maintenance and climate as much as for style.',
      groups: [
        {
          title: 'Deck material options',
          intro: 'Compare heat, maintenance, and lifespan for your yard.',
          options: [
            {
              name: 'Cedar',
              range: 'Natural wood look',
              note: 'Warm appearance with ongoing sealing and maintenance needs.',
            },
            {
              name: 'Composite',
              range: 'Common mid-range pick',
              note: 'Lower maintenance; ask about heat under bare feet in full sun.',
            },
            {
              name: 'Hardwood',
              range: 'Premium',
              note: 'Dense boards with stronger visual presence and higher material cost.',
            },
          ],
        },
        {
          title: 'Shade and cover options',
          intro: 'Shade is often the difference between a used patio and an empty one.',
          options: [
            {
              name: 'Pergola',
              range: 'Open shade',
              note: 'Filtered light with optional fans or lighting.',
            },
            {
              name: 'Covered patio',
              range: 'Full weather cover',
              note: 'Solid roofing for rain and harsh sun.',
            },
            {
              name: 'Screened room',
              range: 'Comfort upgrade',
              note: 'Adds insect protection and a more enclosed outdoor room.',
            },
            {
              name: 'Outdoor kitchen shell',
              range: 'Utility dependent',
              note: 'Plan gas, electric, and ventilation before cabinets and appliances.',
            },
          ],
        },
      ],
    },
    timelines: {
      title: 'Outdoor living timelines',
      intro: 'Schedules are approximate for 2026 Texas projects and depend on weather and material lead times.',
      rows: [
        { tier: 'Simple deck or pergola', duration: 'Often a few weeks', note: 'Faster when access is easy and utilities are not involved.' },
        { tier: 'Covered patio', duration: 'Longer build window', note: 'Roof structure and attachments add steps.' },
        { tier: 'Outdoor kitchen', duration: 'Utility-driven', note: 'Gas and electrical work extend the calendar.' },
        { tier: 'Combined outdoor room', duration: 'Multi-phase', note: 'Deck plus cover plus kitchen stacks trades.' },
      ],
      delaysTitle: 'What commonly extends outdoor schedules',
      delays: [
        'Weather and ground conditions',
        'Material lead times for composite or specialty covers',
        'Utility scheduling for gas and electrical',
        'Permit timing for covered structures where required',
        'Access limits on tight lots',
        'Design changes after footings are set',
      ],
    },
    included: {
      title: 'What an outdoor living project may include',
      intro: 'Scopes vary by deck, patio, pergola, or kitchen goals. Common categories include:',
      checklist: [
        'Site walk and planning',
        'Demolition of existing structures if needed',
        'Footings and framing',
        'Decking or patio surface',
        'Shade or cover structure',
        'Railings and stairs',
        'Electrical and lighting',
        'Gas connections when scoped',
        'Outdoor kitchen elements when scoped',
        'Drainage considerations',
        'Cleanup',
        'Final walkthrough',
      ],
      groups: [
        {
          title: 'Standard scope items',
          body: 'Structure and surfaces sized to the yard - decking, basic railings, and shade where included.',
        },
        {
          title: 'Optional upgrades',
          body: 'Lighting packages, ceiling fans, built-in seating, screens, and outdoor kitchen appliances.',
        },
        {
          title: 'Utility or structural work',
          body: 'Gas, electrical, and heavier roof attachments that need careful site review.',
        },
      ],
    },
    projectTypes: {
      title: 'Types of outdoor living projects',
      intro: 'Name the structure type so the site visit focuses on the right questions.',
      items: [
        { title: 'Deck builders projects', body: 'Ground-level or elevated decks in cedar, composite, or hardwood.' },
        { title: 'Covered patios', body: 'Solid shade and weather cover attached to or near the home.' },
        { title: 'Pergolas', body: 'Open shade structures for filtered light and outdoor lounging.' },
        { title: 'Outdoor kitchens', body: 'Grill islands, counters, and utility-fed cooking zones.' },
        { title: 'Combined outdoor rooms', body: 'Deck plus cover plus kitchen planned as one backyard system.' },
      ],
    },
    process: {
      title: 'Outdoor living process with Epic Home Pros',
      intro: 'One request, a yard walk, and a free written estimate before you commit.',
      steps: [
        {
          label: '1. Initial inquiry',
          body: 'Share city, project type, rough size, and shade or kitchen goals.',
        },
        {
          label: '2. Project discussion',
          body: 'We follow up within about one business day to clarify scope and coverage.',
        },
        {
          label: '3. On-site yard visit',
          body: 'We review sun, drainage, access, and how the structure should connect to the house.',
        },
        {
          label: '4. Written estimate',
          body: 'You receive a written estimate. Free, with no obligation to move forward.',
        },
        {
          label: '5. Material decisions',
          body: 'If you proceed, decking, cover, and utility details lock the final scope.',
        },
        {
          label: '6. Build and completion',
          body: 'Construction, cleanup, and final walkthrough wrap the project.',
        },
      ],
    },
    considerations: {
      title: 'What to consider before building outdoors',
      intro: 'Texas weather should shape the plan as much as style.',
      items: [
        {
          title: 'Sun and heat',
          body: 'Shade and material heat underfoot decide whether the space gets used in summer.',
        },
        {
          title: 'Drainage',
          body: 'Water should move away from the house and outdoor footings.',
        },
        {
          title: 'Maintenance tolerance',
          body: 'Wood looks warm but needs more upkeep than many composite products.',
        },
        {
          title: 'Utility needs',
          body: 'Outdoor kitchens and lighting need outdoor-rated electrical and often gas.',
        },
        {
          title: 'Traffic from indoors',
          body: 'Door locations and flooring transitions affect how people move outside.',
        },
        {
          title: 'Budget tier',
          body: 'Use the 2026 Texas planning ranges, then refine after a site walk.',
        },
      ],
    },
    local: {
      title: 'Outdoor living across Texas',
      intro: 'Climate and lot patterns differ by metro. An on-site walk keeps materials and shade realistic.',
      cities: [
        {
          name: 'Austin',
          slug: 'austin',
          body: 'Austin yards often prioritize shade and heat-aware materials for long summers.',
        },
        {
          name: 'Dallas',
          slug: 'dallas',
          body: 'DFW outdoor rooms need sun planning and storm-ready detailing for North Texas weather.',
        },
        {
          name: 'Houston',
          slug: 'houston',
          body: 'Greater Houston projects should account for humidity, drainage, and summer storms.',
        },
        {
          name: 'San Antonio',
          slug: 'san-antonio',
          body: 'San Antonio outdoor living often focuses on shade, durable surfaces, and evening comfort.',
        },
      ],
      closing: 'Request a quote with your city and project type so we can confirm coverage.',
    },
    gallery: {
      eyebrow: 'Outdoor inspiration',
      title: 'Decks, covers, and outdoor rooms homeowners are planning',
      images: [
        {
          src: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=80&auto=format&fit=crop',
          alt: 'Covered outdoor living patio',
        },
        {
          src: 'https://images.unsplash.com/photo-1600585153490-76fb20a32601?w=1200&q=80&auto=format&fit=crop',
          alt: 'Elevated wood deck with seating',
        },
        {
          src: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200&q=80&auto=format&fit=crop',
          alt: 'Covered patio lounge area',
        },
        {
          src: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&q=80&auto=format&fit=crop',
          alt: 'Outdoor living space connected to home',
        },
      ],
    },
    faqs: [
      {
        question: 'How much does outdoor living cost in Texas in 2026?',
        answer:
          'Composite decks often plan around $8,000-$20,000 depending on size. Covered patios and outdoor kitchens climb from there based on roof structure, electrical, and gas. These are planning ranges, not quotes.',
      },
      {
        question: 'Composite vs wood decks in Texas - what should I ask about?',
        answer:
          'Ask about heat, sun exposure, maintenance, and how the structure handles summer storms. Compare cedar, composite, and hardwood during the site visit.',
      },
      {
        question: 'Do outdoor kitchens need gas and electrical upgrades?',
        answer:
          'Often yes. Outdoor kitchens may need gas lines, outdoor-rated electrical, and proper clearances. That is why an on-site visit matters before a firm number.',
      },
      {
        question: 'Is the outdoor estimate free?',
        answer: 'Yes. Free estimate with no obligation to move forward.',
      },
    ],
    midCta: { title: 'Ready to price an outdoor project?', lead: 'Free on-site estimate. No obligation.' },
    finalCta: {
      title: 'Start your Texas outdoor living quote',
      lead: 'Tell us deck, patio, pergola, or outdoor kitchen goals. We will help with a free on-site estimate.',
    },
  },
};

export function getServiceGuide(slug: string): ServiceGuide | undefined {
  return SERVICE_GUIDES[slug];
}
